import React from 'react';
import { useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  makeStyles,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@material-ui/core';
import { GET_EMAIL_TEMPLATES, GetEmailTemplatesResponse } from '../../core/graphql/queries/emailTemplates';

type TemplateSection = 'Admin' | 'Artist' | 'Streamer' | 'Affiliate' | 'System & external';
type TemplateSource = 'Mailgun' | 'StarFarm HTML' | 'Firebase' | 'Stripe' | 'Planned';

/** Groups rows for the Communications dashboard (single registry for API + external providers). */
function getTemplateSection(triggerEvent: string): TemplateSection {
  const key = String(triggerEvent || '').toLowerCase();
  if (key.startsWith('mailgun.template.')) {
    const mailgunKey = key.replace('mailgun.template.', '');
    if (mailgunKey.startsWith('admin_')) return 'Admin';
    if (mailgunKey.startsWith('affiliate_') || mailgunKey === 'welcome_affiliate') return 'Affiliate';
    if (
      mailgunKey === 'artist' ||
      mailgunKey === 'weekly_activity' ||
      mailgunKey === 'album_request_decline' ||
      mailgunKey === 'albumrequest_decline'
    )
      return 'Artist';
    if (mailgunKey === 'welcome' || mailgunKey === 'welcome_streamers') return 'Streamer';
    return 'System & external';
  }
  if (key.startsWith('admin.')) return 'Admin';
  if (key.startsWith('affiliate.')) return 'Affiliate';
  if (key.startsWith('external.')) return 'System & external';
  if (key.startsWith('artist.') || key.startsWith('track.') || key === 'user.new_follower') return 'Artist';
  if (key.startsWith('streamer.') || key === 'user.signup' || key === 'user.password_reset') return 'Streamer';
  return 'System & external';
}

function getMailgunKey(triggerEvent: string): string | null {
  const key = String(triggerEvent || '');
  if (!key.startsWith('mailgun.template.')) return null;
  return key.replace('mailgun.template.', '') || null;
}

function getTemplateSource(triggerEvent: string): TemplateSource {
  const key = String(triggerEvent || '').toLowerCase();
  if (key.startsWith('mailgun.template.')) return 'Mailgun';
  if (key.startsWith('external.firebase.')) return 'Firebase';
  if (key.startsWith('external.stripe.')) return 'Stripe';
  if (key === 'user.password_reset' || key.startsWith('track.') || key === 'user.new_follower')
    return 'Planned';
  return 'StarFarm HTML';
}

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: '#02172a',
    minHeight: '100vh',
    margin: theme.spacing(-3),
    padding: theme.spacing(3),
  },
  headerTitle: {
    color: '#fff',
    fontWeight: 600,
    fontSize: '2rem',
    marginBottom: theme.spacing(1),
    letterSpacing: '-0.02em',
    fontFamily: '"Syne", "Space Grotesk", sans-serif',
  },
  headerSubtitle: {
    color: '#b0b0b0',
    fontSize: '1rem',
    marginBottom: theme.spacing(3),
  },
  tableWrap: {
    backgroundColor: '#1a2332',
    border: '1px solid #2a3a4a',
    borderRadius: 12,
    overflow: 'hidden',
  },
  headCell: {
    color: '#b0b0b0',
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '1px solid #2a3a4a',
  },
  bodyRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(25, 118, 210, 0.08)',
    },
  },
  bodyCell: {
    color: '#e8e8e8',
    borderBottom: '1px solid #2a3a4a',
    fontSize: '0.9rem',
  },
  muted: {
    color: '#8899aa',
  },
  sectionRow: {
    backgroundColor: 'rgba(25, 118, 210, 0.06)',
  },
  sectionCell: {
    color: '#90caf9',
    fontWeight: 700,
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    borderBottom: '1px solid #2a3a4a',
    paddingTop: 16,
    paddingBottom: 8,
  },
  filterWrap: {
    marginBottom: theme.spacing(2),
  },
  sourceBadge: {
    display: 'inline-block',
    minWidth: 92,
    textAlign: 'center',
    padding: '2px 8px',
    borderRadius: 999,
    fontSize: '0.72rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    border: '1px solid #2a3a4a',
    color: '#c7d4e2',
    backgroundColor: 'rgba(255,255,255,0.02)',
  },
  sourceMailgun: {
    borderColor: '#2f7dd1',
    color: '#9fcfff',
    backgroundColor: 'rgba(47,125,209,0.12)',
  },
}));

function formatOpenRate(sent: number, open: number): string {
  if (!sent) return '—';
  return `${Math.round((open / sent) * 1000) / 10}%`;
}

function formatLastSent(ts: number | null | undefined): string {
  if (ts == null) return '—';
  const d = new Date(ts);
  const days = Math.floor((Date.now() - d.getTime()) / (24 * 60 * 60 * 1000));
  if (days <= 0) return 'Today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

function truncate(s: string, n: number): string {
  if (!s) return '';
  return s.length <= n ? s : `${s.slice(0, n - 1)}…`;
}

const AdminEmailTemplates: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const [mailgunOnly, setMailgunOnly] = React.useState(false);
  const { data, loading, error } = useQuery<GetEmailTemplatesResponse>(GET_EMAIL_TEMPLATES);

  if (loading) {
    return (
      <Box className={classes.root} display="flex" justifyContent="center" alignItems="center" minHeight={320}>
        <CircularProgress style={{ color: '#1976d2' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.root}>
        <Typography className={classes.headerTitle}>Email templates</Typography>
        <Typography style={{ color: '#f44336' }}>{error.message}</Typography>
        <Typography className={classes.headerSubtitle}>
          Ensure the API exposes <code>getEmailTemplates</code> and you are signed in as an admin.
        </Typography>
      </Box>
    );
  }

  const rows = (data?.getEmailTemplates ?? []).filter((row) => {
    if (!mailgunOnly) return true;
    return getTemplateSource(row.triggerEvent) === 'Mailgun';
  });
  const sectionOrder: TemplateSection[] = ['Admin', 'Artist', 'Streamer', 'Affiliate', 'System & external'];
  const grouped = sectionOrder
    .map((section) => ({
      section,
      rows: rows
        .filter((row) => getTemplateSection(row.triggerEvent) === section)
        .sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .filter((g) => g.rows.length > 0);

  return (
    <Box className={classes.root}>
      <Typography className={classes.headerTitle}>Communications — all emails</Typography>
      <Typography className={classes.headerSubtitle}>
        Central registry: Mailgun sends from the API (stats below), plus reference rows for Firebase, Stripe, and
        planned templates. Run <code style={{ color: '#b0e0ff' }}>npm run seed:email-templates</code> in the backend
        to add new catalog entries to the database.
      </Typography>
      <Box className={classes.filterWrap}>
        <FormControlLabel
          control={
            <Checkbox
              checked={mailgunOnly}
              onChange={(e) => setMailgunOnly(e.target.checked)}
              style={{ color: '#90caf9' }}
            />
          }
          label={<span style={{ color: '#c7d4e2' }}>Show only Mailgun-backed templates</span>}
        />
      </Box>

      <TableContainer component={Paper} elevation={0} className={classes.tableWrap}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.headCell}>Name</TableCell>
              <TableCell className={classes.headCell}>Source</TableCell>
              <TableCell className={classes.headCell}>Mailgun key</TableCell>
              <TableCell className={classes.headCell}>Purpose</TableCell>
              <TableCell className={classes.headCell}>Subject</TableCell>
              <TableCell className={classes.headCell}>Last sent</TableCell>
              <TableCell className={classes.headCell} align="right">
                Sent
              </TableCell>
              <TableCell className={classes.headCell} align="right">
                Open rate
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {grouped.map((group) => (
              <React.Fragment key={group.section}>
                <TableRow className={classes.sectionRow}>
                  <TableCell className={classes.sectionCell} colSpan={8}>
                    {group.section}
                  </TableCell>
                </TableRow>
                {group.rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className={classes.bodyRow}
                    onClick={() => history.push(`/admin/emails/${row.id}`)}
                  >
                    <TableCell className={classes.bodyCell}>{row.name}</TableCell>
                    <TableCell className={classes.bodyCell}>
                      <span
                        className={`${classes.sourceBadge} ${
                          getTemplateSource(row.triggerEvent) === 'Mailgun' ? classes.sourceMailgun : ''
                        }`}
                      >
                        {getTemplateSource(row.triggerEvent)}
                      </span>
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      <span className={classes.muted}>{getMailgunKey(row.triggerEvent) || '—'}</span>
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      <span className={classes.muted}>{truncate(row.purpose, 80)}</span>
                    </TableCell>
                    <TableCell className={classes.bodyCell}>{truncate(row.subject, 48)}</TableCell>
                    <TableCell className={classes.bodyCell}>
                      {formatLastSent(row.stats?.lastSentAt ?? null)}
                    </TableCell>
                    <TableCell className={classes.bodyCell} align="right">
                      {row.stats?.sentCount ?? 0}
                    </TableCell>
                    <TableCell className={classes.bodyCell} align="right">
                      {formatOpenRate(row.stats?.sentCount ?? 0, row.stats?.openCount ?? 0)}
                    </TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AdminEmailTemplates;
